var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { Theme } from '@myst-theme/common';
export { Theme };
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function HtmlLink(_a) {
    var { to, className, children, prefetch } = _a, props = __rest(_a, ["to", "className", "children", "prefetch"]);
    return (_jsx("a", Object.assign({ href: to, className: className }, props, { children: children })));
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function HtmlNavLink(_a) {
    var { to, className, children, prefetch } = _a, props = __rest(_a, ["to", "className", "children", "prefetch"]);
    const staticClass = typeof className === 'function' ? className({ isActive: false }) : className;
    return (_jsx("a", Object.assign({ href: to, className: staticClass }, props, { children: children })));
}
export function isTheme(value) {
    return typeof value === 'string' && Object.values(Theme).includes(value);
}
const ThemeContext = React.createContext(undefined);
ThemeContext.displayName = 'ThemeContext';
const prefersLightMQ = '(prefers-color-scheme: light)';
export function ThemeProvider({ children, theme: startingTheme = Theme.light, renderers, Link, top, NavLink, }) {
    const [theme, setTheme] = React.useState(() => {
        if (startingTheme) {
            if (isTheme(startingTheme))
                return startingTheme;
            else
                return null;
        }
        if (typeof document === 'undefined')
            return null;
        return window.matchMedia(prefersLightMQ).matches ? Theme.light : Theme.dark;
    });
    const nextTheme = React.useCallback((next) => {
        if (!next || next === theme || !isTheme(next))
            return;
        if (typeof document !== 'undefined') {
            document.getElementsByTagName('html')[0].className = next;
        }
        const xmlhttp = new XMLHttpRequest();
        xmlhttp.open('POST', '/api/theme');
        xmlhttp.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
        xmlhttp.send(JSON.stringify({ theme: next }));
        setTheme(next);
    }, [theme]);
    return (_jsx(ThemeContext.Provider, { value: { theme, setTheme: nextTheme, renderers, Link, NavLink, top }, children: children }));
}
export function useTheme() {
    const context = React.useContext(ThemeContext);
    if (context === undefined) {
        const error = 'useTheme should be used within a ThemeProvider';
        const throwError = () => {
            throw new Error(error);
        };
        // Just log an error if the theme is asked for
        console.error(error);
        return {
            theme: Theme.light,
            isLight: true,
            isDark: false,
            setTheme: throwError,
            nextTheme: throwError,
        };
    }
    const { theme, setTheme } = context;
    const isDark = theme === Theme.dark;
    const isLight = theme === Theme.light;
    const nextTheme = React.useCallback(() => {
        const next = theme === Theme.light ? Theme.dark : Theme.light;
        setTheme(next);
    }, [theme]);
    return { theme, isLight, isDark, setTheme, nextTheme };
}
export function useNodeRenderers() {
    const context = React.useContext(ThemeContext);
    const { renderers } = context !== null && context !== void 0 ? context : {};
    return renderers !== null && renderers !== void 0 ? renderers : {};
}
export function useLinkProvider() {
    const context = React.useContext(ThemeContext);
    const { Link } = context !== null && context !== void 0 ? context : {};
    return Link !== null && Link !== void 0 ? Link : HtmlLink;
}
export function useNavLinkProvider() {
    const context = React.useContext(ThemeContext);
    const { NavLink } = context !== null && context !== void 0 ? context : {};
    return NavLink !== null && NavLink !== void 0 ? NavLink : HtmlNavLink;
}
export function useThemeTop() {
    const context = React.useContext(ThemeContext);
    const { top } = context !== null && context !== void 0 ? context : {};
    return top || 0;
}
